import React, { useId, useMemo } from "react";
import { useTheme } from "styled-components";
import { monotonePath, Point } from "common/NewLook/monotonePath.ts";

interface NewLookSparklineProps {
  values: number[];
  /** Line colour; defaults to the primary blue. */
  color?: string;
  width?: number;
  height?: number;
  /** Draws the line alone, without the soft fill under it. */
  lineOnly?: boolean;
}

/** A tiny smooth sparkline (no axes, no tooltip) for cards and rows. */
const NewLookSparkline = ({
  values,
  color,
  width = 96,
  height = 36,
  lineOnly,
}: NewLookSparklineProps) => {
  const theme = useTheme();
  const gradientId = `spark-${useId().replace(/[^a-zA-Z0-9-_]/g, "")}`;
  const stroke = color ?? theme.colors.primaryBlue;

  const paths = useMemo(() => {
    const valid = values.filter((value) => Number.isFinite(value));
    if (valid.length < 2) return undefined;
    const min = Math.min(...valid);
    const max = Math.max(...valid);
    // A flat series has nothing to show, and a flat line reads as a broken chart.
    if (max === min) return undefined;
    const range = max - min;
    const points: Point[] = valid.map((value, index) => [
      (index / (valid.length - 1)) * width,
      height - 3 - ((value - min) / range) * (height - 6),
    ]);
    const line = monotonePath(points);
    return { line, area: `${line}L${width},${height}L0,${height}Z` };
  }, [values, width, height]);

  if (!paths) return null;

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      aria-hidden={true}
      style={{ display: "block", overflow: "visible" }}
    >
      {!lineOnly && (
        <>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop
                offset="0%"
                stopColor={stroke}
                stopOpacity={theme.isDarkMode ? 0.2 : 0.14}
              />
              <stop offset="100%" stopColor={stroke} stopOpacity={0} />
            </linearGradient>
          </defs>
          <path d={paths.area} fill={`url(#${gradientId})`} />
        </>
      )}
      <path
        d={paths.line}
        fill="none"
        stroke={stroke}
        strokeWidth={1.5}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
};

export default React.memo(NewLookSparkline);
